import React from 'react'
import styled, { css } from 'styled-components'
import { BaseWrapper, H5, Link } from '@base'
import { Devices, Spacings, Text } from '@styles'
import DonutChart from '../DonutChart'
import { displayAmount } from '@utils'
import { useNavigate } from 'react-router'
import { paths } from '@routes'

const Wapper = styled(BaseWrapper)`
	display: flex;
	width: 100%;
	padding: 1.875rem ${Spacings.SPACING_6B};
	gap: ${Spacings.SPACING_6B};
	flex-direction: column-reverse;
	@media ${Devices.tablet} {
		flex-direction: row;
	}
	${({ FlexColumn }) => {
		if (FlexColumn) {
			return css`
				flex-direction: column;
			`
		}
	}}
`

const ChartContainer = styled.div`
	width: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
`

const CardContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	justify-content: center;
`

const CardCententWapper = styled.div`
	display: flex;
	width: 100%;
	margin: ${Spacings.SPACING_2B} 0;
	flex-direction: column;
	border-radius: ${Spacings.SPACING_2B};
	border: 1px solid rgba(234, 241, 250, 1);
	transition: all 0.2s cubic-bezier(0, 0, 0.2, 1);
	border-color: ${({ theme }) => theme.palette.background.disabled};
`
const CardHeader = styled.div`
	display: flex;
	padding: ${Spacings.SPACING_4B};
	align-items: center;
	gap: ${Spacings.SPACING_2B};
	align-self: stretch;
`
const CartTitle = styled(H5)`
	color: var(--Cool-Gray-60, ${({ theme }) => theme.palette.text.greyThree});
	font-family: Poppins;
	font-size: ${Text.MEDIUM};
	font-style: normal;
	font-weight: ${Text.BOLD_400};
	line-height: 1.375rem;
`

const PriceContainer = styled.div`
	margin: ${Spacings.SPACING_4B};
	display: flex;
	justify-content: space-between;
`

const PriceWapper = styled.p`
	color: var(--Cool-Gray-100, ${({ theme }) => theme.palette.text.greySix});
	font-family: Poppins;
	font-size: ${Text.LARGE};
	font-style: normal;
	font-weight: ${Text.BOLD_500};
	line-height: ${Spacings.SPACING_6B};

	span {
		color: var(--Cool-Gray-50, ${({ theme }) => theme.palette.text.greyTwo});
		font-family: Poppins;
		font-size: ${Text.SMALL};
		font-style: normal;
		font-weight: ${Text.BOLD_500};
		line-height: ${Spacings.SPACING_4B};
	}
`
const PricePercent = styled.p`
	color: var(--Green-60, #198038);
	font-family: Poppins;
	font-size: ${Text.SMALL};
	font-style: normal;
	font-weight: ${Text.BOLD_600};
	line-height: ${Spacings.SPACING_4B};
`

const Divider = styled.hr`
	border: 0.01rem solid rgba(234, 241, 250, 1);
	border-color: ${({ theme }) => theme.palette.background.disabled};
`
const FillWapper = styled.div`
	width: ${Spacings.SPACING_6B};
	height: ${Spacings.SPACING_6B};
	background-color: ${(props) => (props.isActive ? '#64a8ff' : '#001D6C')};
	border-radius: ${Spacings.SPACING_1B};
	box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
`

const TotalAmount = ({ total_amount, over_due_amount, due_amount, organisationName, employeeName, fromDate, toDate }) => {
	const overduePercent = Math.round((over_due_amount / total_amount) * 100) || 0
	const duePercent = Math.round((due_amount / total_amount) * 100) || 0

	const data = [
		{ name: 'Due', value: duePercent, unit: '%' }, // Dark blue
		{ name: 'Overdue', value: overduePercent, unit: '%' }, // Light blue
	]

	const Colors = ['#0A1E6E', '#77B5FF'] // Dark Blue & Light Blue

	const buildLedgerUrl = (baseParams = 'zoho_status=OVERDUE&transaction_source=INVOICE&transaction_type=DEBIT') => {
		const params = new URLSearchParams(baseParams)
		if (organisationName) params.set('organisation', organisationName)
		if (employeeName && employeeName !== 'ALL') params.set('manager_name', employeeName)
		if (fromDate) params.set('from', fromDate)
		if (toDate) params.set('to', toDate)
		return `${paths.admin_ledger}?${params.toString()}`
	}

	return (
		<Wapper>
			<ChartContainer>
				<DonutChart
					total_amount={total_amount}
					data={data}
					Colors={Colors}
					organisationName={organisationName}
					employeeName={employeeName}
					fromDate={fromDate}
					toDate={toDate}
				/>
			</ChartContainer>
			<CardContainer>
				<CardCententWapper>
					<CardHeader>
						<FillWapper isActive />
						<CartTitle>TOTAL OVERDUE</CartTitle>
					</CardHeader>
					<Divider />
					<Link
						to={buildLedgerUrl('zoho_status=OVERDUE&transaction_source=INVOICE&transaction_type=DEBIT')}
						rel='noopener noreferrer'
						target='_blank'
					>
						<PriceContainer>
							<PriceWapper>
								{over_due_amount > 0 ? (
									<>
										{displayAmount(over_due_amount)}
										<span> Cr</span>
									</>
								) : (
									'-'
								)}
							</PriceWapper>
							<PricePercent>{overduePercent}%</PricePercent>
						</PriceContainer>
					</Link>
				</CardCententWapper>

				<CardCententWapper>
					<CardHeader>
						<FillWapper />
						<CartTitle>TOTAL DUE</CartTitle>
					</CardHeader>
					<Divider />
					<Link
						to={buildLedgerUrl('zoho_status=DUE&transaction_source=INVOICE&transaction_type=DEBIT')}
						rel='noopener noreferrer'
						target='_blank'
					>
						<PriceContainer>
							<PriceWapper>
								{due_amount > 0 ? (
									<>
										{displayAmount(due_amount)}
										<span> Cr</span>
									</>
								) : (
									'-'
								)}
							</PriceWapper>
							<PricePercent>{duePercent}%</PricePercent>
						</PriceContainer>
					</Link>
				</CardCententWapper>
			</CardContainer>
		</Wapper>
	)
}

export default TotalAmount
